import { Text } from '@mantine/core';
import { IconPackage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { CriticalStockRow } from './criticalStockData';
import { WidgetEmptyState } from './ReadmodelWidgetStates';
import { componentsTestIds } from '@components/testids';
import classes from './WidgetTable.module.css';

/**
 * TV1-1933 (S18) — the FIRST non-chart widget renderer: a tabular Critical-Stock
 * list. Fed the already-shaped rows (see `criticalStockData.shapeCriticalStockRows`)
 * that `getWidgetData` sourced from the CriticalStock cube. ⚠️ The rows are selected
 * by the cube's seller-declared-umbral predicate, which is a DIFFERENT metric from the
 * retired client-side `0 < runway < 14` cut — not a superset of it. A fast-draining SKU
 * with high absolute stock was on the old list and is deliberately not on this one
 * (D4's accepted trade). Runway is rendered as
 * a coloured badge (redder = fewer days) so the operator can scan "what to restock
 * now" at a glance.
 *
 * TV1-2392 (slice 6) — REGRAINED to (sku, warehouse): the row key is now
 * `sku:warehouseId`, not `sku` alone. ⚠️ Note the direction of causation — `sku`
 * was NOT previously broken. The pre-slice query grouped by `CriticalStock.sku`
 * ALONE (`git show HEAD~:…/curatedReports.ts`: `dimensions: ['CriticalStock.sku']`),
 * so Cube returned exactly one row per SKU and the key was unique by construction.
 * It is THIS slice's regrained query — which adds `CriticalStock.warehouseId` to the
 * dimensions — that makes a bare `sku` ambiguous, because the same SKU can now come
 * back once per warehouse it is critical in. The key moves with the grain that
 * created the ambiguity, ahead of React silently deduping the rows. `warehouseId`
 * is also its own column so two rows for the same SKU read as two different
 * places, not a duplicate. `partialCoverage` renders the same disclosure line
 * as the KPI (`KpiWidget.PartialCoverageNote`): some (sku, warehouse) rows in
 * this account never resolved an umbral, so this list is not the whole catalog.
 */

export type CriticalStockColumn =
  'sku' | 'warehouseId' | 'currentStock' | 'dailyVelocity' | 'daysOfRunway';

export const ALL_CRITICAL_STOCK_COLUMNS: CriticalStockColumn[] = [
  'sku', 'warehouseId', 'currentStock', 'dailyVelocity', 'daysOfRunway'
];

/*
 * TV1-2392 (slice 6) — the label of a column lives NEXT TO the column list, so
 * the props panel's MultiSelect can be derived from `ALL_CRITICAL_STOCK_COLUMNS`
 * instead of re-listing the columns by hand. The hand-written copy in the panel
 * is what let `warehouseId` go missing from the editor while the table already
 * rendered it.
 */
export const CRITICAL_STOCK_COLUMN_LABELS: Record<CriticalStockColumn, [ string, string ]> = {
  sku: [ 'widget_critical_stock_col_sku', 'SKU' ],
  warehouseId: [ 'widget_critical_stock_col_warehouse', 'Depósito' ],
  currentStock: [ 'widget_critical_stock_col_stock', 'Current stock' ],
  dailyVelocity: [ 'widget_critical_stock_col_velocity', 'Units/day' ],
  daysOfRunway: [ 'widget_critical_stock_col_runway', 'Days of runway' ]
};

export type CriticalStockTableProps = {
  rows: CriticalStockRow[];
  columns?: CriticalStockColumn[];
  /** TV1-2392 (slice 6) — see the module docblock. */
  partialCoverage?: boolean;
};

const round = ( value: number ): number => Math.round( value * 10 ) / 10;

/*
 * The three runway bands. Unchanged boundaries (<3 days critical, <7 low, otherwise
 * watch) — what changed is what they resolve to: a CLASS setting one custom
 * property, instead of a Mantine `color="red.6"` string.
 *
 * The shade strings were a real dark-mode defect, not a style preference. `red.6`
 * and `orange.6` are FIXED palette values that do not pass through `light-dark()`,
 * so every runway badge rendered at full saturation against a dark card. The
 * classes resolve through `--w-danger` / `--w-warning`, which are theme-switched,
 * and through the ink scale for the calm band — so a tinted card re-tones them too.
 */
const runwayChipClass = ( days: number | null ): string => {
  // TV1-2392 FIX 4 — no velocity means no runway to band. The calm class, so a
  // non-moving item is never dressed up as "runs out today".
  if ( days === null ) return classes.chipWatch;
  if ( days < 3 ) return classes.chipCritical;
  if ( days < 7 ) return classes.chipLow;
  return classes.chipWatch;
};

export const CriticalStockTable = ({ rows, columns, partialCoverage = false }: CriticalStockTableProps ) => {
  const { t } = useTranslation( 'widgets' );
  const shown = columns?.length ? columns : ALL_CRITICAL_STOCK_COLUMNS;

  const headers = Object.fromEntries(
    ALL_CRITICAL_STOCK_COLUMNS.map( column => [ column, t( ...CRITICAL_STOCK_COLUMN_LABELS[column]) ])
  ) as Record<CriticalStockColumn, string>;

  // TV1-2392 (slice 6) — same disclosure as the KPI's `PartialCoverageNote`,
  // independent of whether the table is empty or full: some rows behind this
  // account never resolved an umbral, so neither state is the whole picture.
  const partialCoverageNote = partialCoverage ? (
    <Text
      component="div"
      className={classes.numeric}
      data-testid={componentsTestIds.criticalStockPartialCoverage}
    >
      {t(
        'widget_critical_stock_partial_coverage',
        'Hay ítems sin un umbral de stock configurado; no aparecen en esta lista.'
      )}
    </Text>
  ) : null;

  if ( !rows.length ) {
    /*
     * ⚠️ AN EMPTY CRITICAL-STOCK TABLE IS GOOD NEWS, and the copy has to say so.
     *
     * It read "No SKUs below the runway threshold." in a dimmed grey line in the
     * corner — technically true, and indistinguishable from the eight other ways a
     * card can have nothing in it. This tile answers "what am I about to run out
     * of?", and the answer "nothing" is the ANSWER, not a failure to load, not a
     * misconfiguration and not an empty filter. The title now states the outcome
     * and the description states the condition that produced it.
     */
    return (
      <>
        <WidgetEmptyState
          icon={<IconPackage size={20} />}
          title={t( 'widget_critical_stock_empty_title', 'Sin quiebres a la vista' )}
          description={t(
            'widget_critical_stock_empty',
            'Ningún ítem está por debajo de su umbral de stock configurado.'
          )}
          testId={componentsTestIds.criticalStockWidgetEmpty}
        />
        {partialCoverageNote}
      </>
    );
  }

  return (
    <div className={classes.scroller}>
      {partialCoverageNote}
      <table className={classes.table}>
        <thead>
          <tr>
            {shown.map( col => (
              <th key={col} className={col === 'sku' || col === 'warehouseId' ? undefined : classes.numeric}>
                {headers[col]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* TV1-2392 — key is `sku:warehouseId`, not `sku` alone, because THIS
              slice's query added `warehouseId` to the dimensions: the same SKU
              can NOW appear more than once (once per warehouse it is critical
              in). It could not before — the old query grouped by `sku` alone —
              so this is a key following a new grain, not a bug being fixed. */}
          {rows.map( row => (
            <tr key={`${ row.sku }:${ row.warehouseId }`}>
              {shown.map( col => {
                if ( col === 'sku' ) return <td key={col}>{row.sku}</td>;
                if ( col === 'warehouseId' ) return <td key={col}>{row.warehouseId}</td>;
                if ( col === 'daysOfRunway' ) {
                  return (
                    <td key={col} className={classes.numeric}>
                      <span className={`${ classes.chip } ${ runwayChipClass( row.daysOfRunway ) }`}>
                        {row.daysOfRunway === null ? '—' : round( row.daysOfRunway )}
                      </span>
                    </td>
                  );
                }
                return <td key={col} className={classes.numeric}>{round( row[col] as number )}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
